import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useParams, Link } from 'react-router-dom';
import { getComicById, getLatestComics } from '@/mocks/comics';

function formatViews(views: number): string {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)}万`;
  }
  return views.toString();
}

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(Number(id));
  const relatedComics = getLatestComics().filter(c => c.id !== Number(id)).slice(0, 6);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-10">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">该漫画不存在或已被删除</p>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 font-medium cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ComicStory',
    name: comic.title,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    description: comic.description,
    url: `http://tangcruises.com/comic/${comic.id}`,
    image: comic.cover,
    dateModified: comic.updatedAt,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: comic.rating,
      bestRating: '10',
      ratingCount: Math.floor(comic.views / 100),
    },
    genre: comic.tags,
    inLanguage: 'zh-CN',
  };

  const webPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${comic.title} - 073漫画网`,
    description: `${comic.title}漫画在线免费阅读，作者${comic.author}，${comic.description}`,
    url: `http://tangcruises.com/comic/${comic.id}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '073漫画网',
      url: 'http://tangcruises.com/',
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'http://tangcruises.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: comic.tags?.[0] || '漫画',
        item: `http://tangcruises.com/category/${comic.category}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: comic.title,
        item: `http://tangcruises.com/comic/${comic.id}`,
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(webPageJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-10">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: comic.tags?.[0] || '漫画', href: `/category/${comic.category}` },
                { label: comic.title },
              ]}
            />

            <article className="mt-6">
              <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                <div className="flex-shrink-0">
                  <div className="w-48 md:w-56 aspect-[3/4] rounded-xl overflow-hidden">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      title={`${comic.title} - 073漫画网`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                    {comic.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="text-sm text-foreground-600">作者：<strong>{comic.author}</strong></span>
                    <span className="text-foreground-300">|</span>
                    <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full whitespace-nowrap ${
                      comic.status === '已完结'
                        ? 'bg-accent-100 text-accent-700'
                        : 'bg-primary-100 text-primary-700'
                    }`}>
                      {comic.status}
                    </span>
                    <span className="text-foreground-300">|</span>
                    <span className="text-sm text-foreground-600">共 <strong>{comic.chapters}</strong> 话</span>
                  </div>

                  <div className="flex flex-wrap items-center gap-4 mb-4">
                    <div className="flex items-center gap-1.5">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-400 text-sm"></i>
                      </div>
                      <span className="text-sm font-bold text-foreground-900">{comic.rating}</span>
                      <span className="text-xs text-foreground-500">分</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-eye-line text-foreground-400 text-sm"></i>
                      </div>
                      <span className="text-sm text-foreground-600">{formatViews(comic.views)}次阅读</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-time-line text-foreground-400 text-sm"></i>
                      </div>
                      <span className="text-sm text-foreground-600">最近更新：{comic.updatedAt}</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {comic.tags?.map((tag) => (
                      <span key={tag} className="text-xs bg-secondary-100 text-secondary-700 px-3 py-1 rounded-full whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <p className="text-sm text-foreground-600 leading-relaxed mb-6 max-w-2xl">
                    {comic.description}
                  </p>

                  <div className="flex flex-wrap gap-3">
                    <Link
                      to={`/comic/${comic.id}`}
                      className="px-6 py-2.5 bg-primary-500 text-background-50 font-medium rounded-full text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      开始阅读
                      <i className="ri-play-line ml-1.5"></i>
                    </Link>
                    <button className="px-6 py-2.5 bg-secondary-100 text-secondary-700 font-medium rounded-full text-sm hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer">
                      <i className="ri-bookmark-line mr-1.5"></i>
                      加入收藏
                    </button>
                    <button className="px-6 py-2.5 bg-background-100 text-foreground-600 font-medium rounded-full text-sm hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                      <i className="ri-share-forward-line mr-1.5"></i>
                      分享
                    </button>
                  </div>
                </div>
              </div>

              <section className="mt-10">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-heading font-bold text-foreground-900">章节列表</h2>
                  <div className="flex items-center gap-2">
                    <button className="text-xs text-foreground-500 bg-background-100 hover:bg-background-200 px-3 py-1.5 rounded-full transition-colors whitespace-nowrap cursor-pointer">
                      正序
                    </button>
                    <button className="text-xs text-primary-500 bg-primary-50 px-3 py-1.5 rounded-full transition-colors whitespace-nowrap cursor-pointer">
                      倒序
                    </button>
                  </div>
                </div>

                <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-background-200/70">
                    {comic.chapters_list?.map((chapter) => (
                      <a
                        key={chapter.num}
                        href={`#chapter-${chapter.num}`}
                        className="flex items-center justify-between p-3.5 bg-background-50 hover:bg-background-100/70 transition-colors cursor-pointer"
                      >
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-foreground-800 line-clamp-1">
                            第{chapter.num}话 {chapter.title}
                          </span>
                        </div>
                        <span className="text-xs text-foreground-400 flex-shrink-0 ml-2">{chapter.date}</span>
                      </a>
                    ))}
                  </div>
                </div>

                <div className="text-center mt-6">
                  <button className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer">
                    展开全部章节（共{comic.chapters}话）
                    <i className="ri-arrow-down-s-line ml-1"></i>
                  </button>
                </div>
              </section>

              {relatedComics.length > 0 && (
                <section className="mt-12">
                  <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">猜你喜欢</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {relatedComics.map((rc) => (
                      <Link key={rc.id} to={`/comic/${rc.id}`} className="group cursor-pointer">
                        <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-2">
                          <img
                            src={rc.cover}
                            alt={rc.title}
                            title={`${rc.title} - 073漫画网`}
                            className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                            loading="lazy"
                          />
                        </div>
                        <h3 className="text-xs font-medium text-foreground-800 line-clamp-1 group-hover:text-primary-500 transition-colors">
                          {rc.title}
                        </h3>
                        <p className="text-xs text-foreground-500 mt-0.5">{rc.author}</p>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </article>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}